import React, { Fragment, useContext, useState, useEffect } from 'react';
import accounting from 'accounting';
import classnames from 'classnames';
import { titleize } from 'underscore.string';
import moment from 'moment';
import i18next from 'i18next';
import { Transition, Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import { useTranslation } from 'react-i18next';
import {
  DistanceUnitFormatter,
  LoadDimensionFormatter,
  LoadUnitFormatter,
} from 'components/modules/master/formatters';
import { DEFAULT_DISTANCE_UNIT } from 'utils/localization/types';
import formatMoney from 'utils/formatters/Money';
import { isIntermodalShipmentType } from '../../helpers/shipment-types';
import { acceptOffer } from '../../utils/DriverWebAPIUtils';
import LoadingCircle from '../ui/LoadingCircle';
import CalendarIcon from '../v2/icons/CalendarIcon';
import TruckIcon from '../v2/icons/TruckIcon';
import ClipboardListIcon from '../v2/icons/ClipboardListIcon';
import DocumentIcon from '../v2/icons/DocumentIcon';
import ExclamationIcon from '../v2/icons/ExclamationIcon';
import IconTextDisplay from '../v2/IconTextDisplay';
import FSCDislaimer from '../modules/pricing/fsc-disclaimer';
import Alert from '../ui/Alert';
import OfferStop from './OfferStop';
import AssignDriverModal from '../modules/shipments/AssignDriverModal';
import { checkShorthaulAppointmentNeeded } from 'helpers/shipments';

const DATE_FORMAT = 'L';

const OfferDetails = ({
  carrier,
  offer,
  drivers,
  accessorialsLink,
  onHide,
  onAssignDriverSuccess,
  disabled,
  admin,
  handleOpenModal,
  openModal,
}) => {
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [driver, setDriver] = useState({});
  const [assignDriverRequested, setAssignDriverRequested] = useState(false);
  const intermodalShipmentType = isIntermodalShipmentType(offer);
  const [showFSCDisclaimer, setShowFSCDisclaimer] = useState(false);
  const [error, setError] = useState('');

  const { t } = useTranslation();

  useEffect(() => {
    setShowFSCDisclaimer(!!offer?.reload_fsc);
  }, [offer?.reload_fsc]);

  let equipmentStr;

  if (!intermodalShipmentType) {
    equipmentStr = (offer.equipment_type || []).map((eq) => t(eq)).join(', ');
  }
  const formattedCost = formatMoney(
    offer.rate ? offer.rate : offer.price || 0,
    offer.currency,
  );
  const formattedRatePerMile = offer.rate_per_mile
    ? formatMoney(offer.rate_per_mile, offer.currency)
    : null;
  const formattedDistance = accounting.formatNumber(offer.distance);

  function handleAcceptOffer() {
    setOfferAccepted(true);
  }

  function handleSelectDriver(selected) {
    if (!selected) {
      setDriver({});
    } else {
      const selectedDriver = drivers.find((d) => d._id === selected);
      if (selectedDriver) {
        setDriver(selectedDriver);
      }
    }
  }

  async function handleAssignDriver() {
    setAssignDriverRequested(true);
    try {
      await acceptOffer({ offerId: offer._id, driverId: driver._id });
      setAssignDriverRequested(false);
      setError('');
      onHide();
      onAssignDriverSuccess(driver);
    } catch (e) {
      console.log(e);
      setAssignDriverRequested(false);
      setError(`${t('PROBLEM_ASSIGNING_OFFER')}: ${t(e?.entity?.error)}`);
    }
  }

  function handleViewAccessorialRates() {
    if (accessorialsLink) {
      window.open(accessorialsLink, 'blank').focus();
    }
  }

  function handleCancel() {
    setOfferAccepted(false);
  }

  function handleClose() {
    onHide();
  }

  function handleCloseFSCDisclaimer() {
    setShowFSCDisclaimer(false);
  }

  function renderAssignCarrierModal() {
    return (
      <div>
        <AssignDriverModal
          shipments={[]}
          shipment={offer}
          show={openModal}
          onAssignDriver={ShipmentsActionCreators.assignDriver}
          onHide={() => {
            handleOpenModal(false);
          }}
          assignCarrier
        />
      </div>
    );
  }

  function formatLoad(load) {
    let type = '';
    if (load.type === 'boxes') {
      type = titleize(t('BOX', { count: load?.quantity || 1 }));
    } else if (load.type === 'pallets') {
      type = titleize(t('PALLET', { count: load?.quantity || 1 }));
    }

    const { length, width, height, quantity, dimensional_units } = load;
    return (
      <LoadDimensionFormatter
        quantity={quantity}
        type={type}
        length={length}
        width={width}
        height={height}
        dimensional_units={dimensional_units}
      />
    );
  }

  function getServiceType(slug) {
    if (slug === 'live') {
      return t('LIVE_LOAD_UNLOAD');
    }
    return t('DROP_HOOK');
  }

  const stops = offer.stops || [];
  const stopEquipments = stops.length > 0 ? stops[0].equipments || [] : [];
  const hasStopEquipment = stopEquipments.length > 0 && stopEquipments[0];
  const { status, type } = offer;
  const disableBtn = checkShorthaulAppointmentNeeded(type, status);
  return (
    <div>
      {renderAssignCarrierModal()}

      <div className="flex flex-col justify-between h-full">
        <div className="overflow-y-auto">
          <div className="px-4 py-5 text-white bg-green-cm">
            <div className="flex justify-between">
              <div>
                <h2 className="my-0 text-2xl">{formattedCost}</h2>
                {offer.rate_per_mile && (
                  <h4 className="my-0 mt-1 mb-1 text-base text-green-50">
                    {t('UNIT_PER_MILE', { ratePerMile: formattedRatePerMile })}
                  </h4>
                )}
                <a
                  href={`/shipments/${offer._id}`}
                  className="my-0 mb-1 text-base text-rose-800"
                >
                  {offer.shipment_id}
                </a>
                {offer.hazmat_description && (
                  <IconTextDisplay
                    className="items-center"
                    color="red"
                    icon={<ExclamationIcon />}
                  >
                    <dl className="text-left">
                      <dt className="mb-2 text-white form-label">
                        {t('HAZMAT_LOAD')}
                      </dt>
                    </dl>
                  </IconTextDisplay>
                )}
              </div>
              <div>
                <h3 className="my-0 text-lg text-white">
                  <DistanceUnitFormatter
                    toUnit={DEFAULT_DISTANCE_UNIT}
                    distance={formattedDistance}
                  />
                </h3>
                {equipmentStr && (
                  <h4 className="my-0 mt-1 mb-3 truncate text-green-50">
                    {equipmentStr}
                  </h4>
                )}
                {intermodalShipmentType &&
                  offer.order &&
                  offer.order.service_type && (
                    <h4 className="my-0 mt-1 mb-3 text-green-50">
                      {getServiceType(offer.order.service_type)}
                    </h4>
                  )}
                <p className="my-0 text-green-50">
                  {t('POSTED_DATE', {
                    date: moment(offer.offerCreationDate).fromNow(),
                  })}
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-16 overflow-hidden">
              <div className="h-11 w-11 bg-green-cm -rotate-45 transform origin-top-left" />
            </div>
          </div>
          {showFSCDisclaimer && (
            <div className="px-4">
              <FSCDislaimer onHide={handleCloseFSCDisclaimer} />
            </div>
          )}
          <div className="px-4 py-5 sm:p-8 space-y-6">
            {offer.stops.map((stop) => (
              <OfferStop stop={stop} key={stop.sequence} className="" />
            ))}
          </div>
          <div className="px-4 py-5">
            <div className="w-full">
              <div className="mb-6 sm:grid sm:gap-x-12 sm:gap-y-4 sm:grid-cols-2">
                {intermodalShipmentType && (
                  <div className="">
                    <IconTextDisplay color="blue" icon={<CalendarIcon />}>
                      <div className="flex flex-wrap w-64 -mx-2">
                        {offer.estimated_arrival && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('ETA')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.estimated_arrival).format(
                                DATE_FORMAT,
                              )}
                            </dd>
                          </dl>
                        )}
                        {offer.last_cut_date && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('LAST_CUT_DATE')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.last_cut_date).format(DATE_FORMAT)}
                            </dd>
                          </dl>
                        )}

                        {offer.arrival_date && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('ARRIVAL_DATE')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.arrival_date).format(DATE_FORMAT)}
                            </dd>
                          </dl>
                        )}
                        {offer.discharge_date && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('DISCHARGE_DATE')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.discharge_date).format(DATE_FORMAT)}
                            </dd>
                          </dl>
                        )}

                        {offer.last_free_date && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('LAST_FREE_DATE')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.last_free_date).format(DATE_FORMAT)}
                            </dd>
                          </dl>
                        )}

                        {offer.empty_returned_date && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('EMPTY_RETURNED_DATE')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.empty_returned_date).format(
                                DATE_FORMAT,
                              )}
                            </dd>
                          </dl>
                        )}

                        {offer.outgated_date && (
                          <dl className="w-48 px-2 mb-2">
                            <dt className="text-sm font-medium text-gray-500">
                              {t('OUTDATED_DATE')}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {moment(offer.outgated_date).format(DATE_FORMAT)}
                            </dd>
                          </dl>
                        )}
                      </div>
                    </IconTextDisplay>
                  </div>
                )}
                <div className="">
                  <IconTextDisplay className="" icon={<TruckIcon />}>
                    <div>
                      {intermodalShipmentType && hasStopEquipment && (
                        <div className="w-32 mb-1 text-sm font-medium text-gray-700 truncate">
                          {stopEquipments[0]._type &&
                            stopEquipments[0]._type + ' '}
                          {stopEquipments[0]._equipmentSize}&apos;
                        </div>
                      )}
                      {!intermodalShipmentType && (
                        <div className="mb-1 text-sm">
                          <p
                            className="w-32 mb-1 font-medium text-gray-700 truncate"
                            title={equipmentStr}
                          >
                            {equipmentStr}
                          </p>
                          {offer.loads.map((load, i) => (
                            <p className="mb-1 text-gray-500" key={i}>
                              {formatLoad(load)}
                            </p>
                          ))}
                        </div>
                      )}
                      <div className="w-24 text-gray-500">
                        <LoadUnitFormatter
                          weight={accounting.formatNumber(offer.weight)}
                          weight_units={offer.weight_units}
                        />
                      </div>
                    </div>
                  </IconTextDisplay>
                </div>
                <div>
                  {offer.hazmat_description && (
                    <IconTextDisplay
                      className=""
                      color="red"
                      icon={<ExclamationIcon />}
                    >
                      <dl className="text-left">
                        <dt className="mb-2 form-label">{t('HAZMAT_LOAD')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {offer.hazmat_description}
                        </dd>
                      </dl>
                    </IconTextDisplay>
                  )}
                </div>
              </div>
              {offer.notes && (
                <div>
                  <IconTextDisplay
                    color="blue"
                    className=""
                    icon={<ClipboardListIcon />}
                  >
                    <dl className="w-48 mb-2">
                      <dt className="text-sm font-medium text-gray-500">
                        {titleize(t('NOTE', { count: 2 }))}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {offer.notes}
                      </dd>
                    </dl>
                  </IconTextDisplay>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 py-5 sm:p-8 bg-gray-50">
          {!offerAccepted && (
            <div className="flex-grow-0 flex-shrink-0 px-2 space-x-2">
              {!carrier && (
                <div className="mb-2 -mx-4 flex">
                  <Alert type="info">
                    {t('SELECT_CARRIER_TO_ACCEPT_OFFERS')}
                  </Alert>
                  {admin && (
                    <button
                      className="pb-4 ml-2 mt-2 h-10 block button button-green"
                      disabled={disableBtn}
                      onClick={() => {
                        handleOpenModal(true);
                      }}
                    >
                      {t('ASSIGN_CARRIER')}
                    </button>
                  )}
                </div>
              )}

              {carrier && disabled && (
                <Alert type="info" className="mb-2">
                  <a
                    href={`/users/${carrier}`}
                    target="_blank"
                    rel="noreferrer nofollow"
                  >
                    {t('CREATE_DRIVER_TO_ACCEPT_OFFERS')}
                  </a>
                </Alert>
              )}
              <div className="flex flex-wrap justify-between mb-12 -mx-4">
                <button type="button px-2" onClick={handleViewAccessorialRates}>
                  <IconTextDisplay
                    color="blue"
                    className="items-center"
                    icon={<DocumentIcon />}
                  >
                    <dl className="text-left">
                      <dt className="form-label">{t('ACCESSORIAL_RATES')}</dt>
                    </dl>
                  </IconTextDisplay>
                </button>
                <div className="flex flex-grow-0 flex-shrink-0 px-2 space-x-2">
                  <button
                    className="flex-grow-0 flex-shrink-0 block button button-green"
                    type="button"
                    disabled={disabled || disableBtn}
                    onClick={handleAcceptOffer}
                  >
                    {t('ACCEPT_OFFER')}
                  </button>
                  <button
                    className="flex-grow-0 flex-shrink-0 block button button-secondary"
                    type="button"
                    onClick={handleClose}
                  >
                    {titleize(t('CLOSE'))}
                  </button>
                </div>
              </div>
            </div>
          )}
          {offerAccepted && (
            <div className="relative">
              {error && (
                <Alert type="error" className="my-2">
                  {error}
                </Alert>
              )}
              <div className="flex items-center justify-end mb-4 space-x-4">
                <Listbox
                  value={driver.id}
                  onChange={handleSelectDriver}
                  className="w-64"
                >
                  <div className="relative mt-1">
                    <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-green-cm focus-visible:ring-offset-2 focus-visible:border-green-cm sm:text-sm">
                      <span className="block truncate">
                        {driver && driver.name
                          ? driver.name
                          : t('SELECT_A_DRIVER')}
                      </span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronUpDownIcon
                          className="w-5 h-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute w-64 py-1 mt-1 overflow-auto text-base bg-white shadow-lg rounded-md max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {drivers.map((authorizedDriver) => (
                          <Listbox.Option
                            key={authorizedDriver.id}
                            value={authorizedDriver.id}
                            className={({ active }) =>
                              classnames(
                                {
                                  'text-green-900': active,
                                  'bg-green-50': active,
                                  'text-gray-900': !active,
                                },
                                'cursor-default select-none relative py-2 pl-10 pr-4',
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={classnames(
                                    {
                                      'font-medium': selected,
                                      'font-normal': !selected,
                                    },
                                    'block truncate',
                                  )}
                                >
                                  {authorizedDriver.name}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-green-600">
                                    <CheckIcon
                                      className="w-5 h-5"
                                      aria-hidden="true"
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
                <div className="flex items-center flex-none">
                  <button
                    className="button button-green"
                    onClick={handleAssignDriver}
                    disabled={!driver || !driver.id || assignDriverRequested}
                  >
                    {!assignDriverRequested && (
                      <span>{t('ASSIGN_DRIVER')}</span>
                    )}
                    {assignDriverRequested && (
                      <LoadingCircle text={t('ASSIGNING_DRIVER')} />
                    )}
                  </button>
                  <button
                    className="block button button-secondary"
                    type="button"
                    disabled={assignDriverRequested}
                    onClick={handleCancel}
                  >
                    {t('CANCEL')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferDetails;
